import {observable, toJS} from 'mobx';
import {AnalyticsTab, Store} from './store';
import {TableSortDirection} from '../types/Table';
import {KPI, KPIGroupKey, ProductKPI, SearchtermKPI, SessionInsightKPI} from '../kpi';
import {DateRange} from './TimeIntervalStore';
import {Channel} from '../types/Channel';
import {ShowInsightsKPI} from './GeneralSelectorTableStore';

interface InsightsTableSort {
    attribute: SessionInsightKPI | KPIGroupKey.Searchterm;
    sortDirection: TableSortDirection;
}

export interface DataForPrimaryAndSecondaryAttribute extends GeneralSessionTableData {
    id: string;
    [KPIGroupKey.Searchterm]: string;
}

export interface GeneralSessionTableData {
    [SessionInsightKPI.SearchEvents]: number;
    [SessionInsightKPI.CartEvents]: number;
    [SessionInsightKPI.CartTotalAmount]: number;
    [SessionInsightKPI.CheckoutEvents]: number;
    [SessionInsightKPI.CheckoutTotalAmount]: number;
    [SearchtermKPI.ClickCount]: number;
    [ProductKPI.Sales]: number;
}

export enum InsightsTableMode {
    SearchTermsForProduct = 'searchTermsForProduct',
    ProductsForSearchTerm = 'productsForSearchTerm',
}

export interface InsightsModalSetting {
    mainDateRange?: DateRange;
    primary: string;
    secondary: KPI;
    channel: Channel;
    sessionApiKey: SessionInsightKPI;
    tab: AnalyticsTab;
    mode: InsightsTableMode;
}

interface InsightsTableData {
    loading: boolean;
    data: DataForPrimaryAndSecondaryAttribute[];
}
class InsightsTableStore {
    // used to manage state of InsightsTable for both searches and products

    constructor(private readonly mainStore: Store) {}

    @observable public insightsModalSetting: InsightsModalSetting | null = null;
    @observable
    public insightsTableData: InsightsTableData = {
        loading: false,
        data: [],
    };

    // states for the table itself
    @observable
    public tableSorting: InsightsTableSort = {
        sortDirection: TableSortDirection.DESC,
        attribute: SessionInsightKPI.Sales,
    };

    public cellAttributeContextMenu: any;

    public getSessionApiKeyIfAvailable = (dataKey: string): string => {
        const {selectorTableStore} = this.mainStore;
        const matchingColumn = selectorTableStore.tableColumnList.find((column) => column.dataKey === dataKey);
        if (matchingColumn !== undefined && matchingColumn.sessionApiKey !== undefined) {
            return matchingColumn.sessionApiKey;
        }
        return dataKey;
    };

    public setTableSort = (sortAttribute: SessionInsightKPI, sortDirection: TableSortDirection) => {
        this.tableSorting = {
            sortDirection,
            attribute: sortAttribute,
        };
        this.insightsTableData.data = this.sortForPrimaryAndSecondaryAttribute(toJS(this.insightsTableData.data));
    };

    public sortForPrimaryAndSecondaryAttribute(sessionData: DataForPrimaryAndSecondaryAttribute[]): DataForPrimaryAndSecondaryAttribute[] {
        const {attribute, sortDirection} = this.tableSorting;

        sessionData.sort((a, b) => {
            if (attribute === KPIGroupKey.Searchterm) {
                return a[attribute].localeCompare(b[attribute]);
            }

            return a[attribute] - b[attribute];
        });

        if (sortDirection === TableSortDirection.DESC) {
            sessionData.reverse();
        }

        return sessionData;
    }

    public openInsightsModalAndLoadData(setting: {
        mainDateRange?: DateRange;
        primary: string;
        secondary: KPI;
        channel?: Channel;
        sessionApiKey: ShowInsightsKPI;
        tab: AnalyticsTab;
        mode: InsightsTableMode;
    }) {
        if (!setting.channel) {
            return;
        }

        const sessionApiKey = this.mainStore.selectorTableStore.getSessionApiKeyIfPossible(setting.sessionApiKey);

        this.insightsModalSetting = {
            ...setting,
            channel: setting.channel,
            sessionApiKey,
        };

        this.tableSorting = {
            sortDirection: TableSortDirection.DESC,
            attribute: sessionApiKey,
        };

        this.insightsTableData = {
            loading: true,
            data: [],
        };

        switch (this.insightsModalSetting.mode) {
            case InsightsTableMode.SearchTermsForProduct:
            case InsightsTableMode.ProductsForSearchTerm:
                this.mainStore.selectorTableStore.fetchInsightsData(
                    this.insightsModalSetting.primary,
                    this.insightsModalSetting.secondary,
                    this.insightsModalSetting.channel
                );
                return;

            default:
                throw Error('None supported mode selected for insights ' + this.insightsModalSetting.mode);
        }
    }

    public closeModal() {
        this.cellAttributeContextMenu = null;
        this.insightsModalSetting = null;

        this.tableSorting = {
            sortDirection: TableSortDirection.DESC,
            attribute: SessionInsightKPI.Sales,
        };

        this.insightsTableData = {
            loading: false,
            data: [],
        };
    }
}

export default InsightsTableStore;



// WEBPACK FOOTER //
// ./src/stores/InsightsTableStore.ts